<template>
  <!-- Use :class binding for adding dynamic classes -->
  <BaseModal class="modal-contact" modal-id="modalContact" :show-modal="props.showModal">
    <template #heading>Send a confirmation and reminder</template>
    <template #content>
      <span></span>
      <form>
        <ContactPhone :is-modal="true" @doPhone="setPhone" @doResetVerify="resetSendNow" :readonly="!customerDetail" :disabled="!customerDetail" />
        <p hidden ref="invalidPhoneMsg" class="app-invalid-msg bad-phone">Please enter a valid phone number.</p>
        <ContactEmail :is-modal="true" @doEmail="setEmail" @doResetVerify="resetSendNow" :readonly="!customerDetail" :disabled="!customerDetail" />
        <p hidden ref="noContactInfo" class="app-invalid-msg msg-offset">
          Either phone or email is required.
        </p>
      </form>
    </template>
    <template #footing>
      <div class="footer-offset">
        <ModalDisclaimer @doDisclaimer="adjustModalHeight" />
        <BaseButtonGroup>
          <button type="button" @click="closeModal">{{ language.btnCancel }}</button>
          <button type="submit" class="app-btn-primary" @click.prevent="notifyMe">{{ language.btnSend }}</button>
        </BaseButtonGroup>
      </div>
    </template>
  </BaseModal>
</template>


<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import BaseModal from '@/components/ui/BaseModal';
import ContactPhone from '@/components/reservation/user/contact/ContactPhone';
import ContactEmail from '@/components/reservation/user/contact/ContactEmail';
import ModalDisclaimer from '@/components/reservation/confirmation/ModalDisclaimer';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';

// Props and Emits
const props = defineProps({
  showModal: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['doClose', 'doNotificationMessage']);

// Composables
const store = useStore();
const { formatPhoneForRims } = usePhoneFormatter();

// Reactive State
const myPhone = ref('');
const myEmail = ref('');
const isPhoneValidated = ref(true);
const isEmailValidated = ref(true);
const sendNow = ref(false);
const isModify = ref(false);

// Template Refs
const noContactInfo = ref(null);
const invalidPhoneMsg = ref(null);

// Computed Properties
const language = computed(() => store.state.user.LANG);
const resAppointmentNum = computed(() => store.state.user.resAppointmentNum);
const resDetails = computed(() => store.state.user.resDetails);
const customerDetail = computed(() => store.state.user.customerDetail);

// Lifecycle Hook
onMounted(async () => {
  isModify.value = await store.dispatch('user/isReservationForUpdate');
});

// Watcher
watch([isEmailValidated, isPhoneValidated], () => {
  if (isPhoneValidated.value && isEmailValidated.value && sendNow.value) {
    sendNotification();
  }
});

// Methods
const adjustModalHeight = (e) => {
  const collapsed = e;
  nextTick(() => {
    const modalContent = document.querySelector('.app-modal-content');
    const overflowSelector = '__overflow';
    if (collapsed) {
      modalContent.classList.remove(overflowSelector);
    }
    else if (screen.height > 400 && modalContent.offsetHeight > (screen.height * 0.9)) {
      modalContent.classList.add(overflowSelector);
    }
  });
};

const closeModal = () => {
  sendNow.value = false;
  // close modal so that network error message from middle tier will be displayed unblocked
  emit('doClose');
};

const hasContactInfo = () => {
  return (myPhone.value.length > 0 || myEmail.value.length > 0);
};

const handleNotificationResponse = (response) => {
  emit('doNotificationMessage', {
    isConsentProvided: true,
    isEmailSentSuccessfully: response.isEmailSentSuccessfully,
    isSmsSentSuccessfully: response.isSmsSentSuccessfully,
  });

  // close modal - let parent take care of notification status
  closeModal();
};

/**
 * User hits the Send button.
 */
const notifyMe = () => {
  sendNow.value = true;
  sendNotification();
};

const resetSendNow = () => {
  // prevent auto send upon valid validations from children's watchers
  sendNow.value = false;
};

/**
 * Request backend to send notification(s) if everything checks out.
 */
const sendNotification = async () => {
  // workaround - emulate child component's phone validation
  if (!validatePhoneAgain()) {
    sendNow.value = false;
    return;
  }

  // show/hide required field message
  const hasContact = hasContactInfo();
  toggleContactInfoCheck(hasContact);

  // send notification IFF at least one good contact is provided and there are no standing errors
  if (sendNow.value && isEmailValidated.value && isPhoneValidated.value && hasContact) {
    const formattedPhoneNum = formatPhoneForRims(myPhone.value);
    const data = {
      appointmentNumber: resAppointmentNum.value,   // "required" only for RIMS email/sms notifications (?)
      email: myEmail.value,
      isCancellationConfirmation: false,            // for cxl reservation
      isConsentProvided: true,
      phone: formattedPhoneNum,
    };

    // user might have overridden their info...
    updateContactInfo(data);

    // wait for send response...
    const response = await store.dispatch('user/sendConfirmation', data);
    handleNotificationResponse(response);
  }

  // prevent data from being submitted without user's explicit choice
  resetSendNow();
};

/**
 * Depend on ContactEmail myEmail watcher.
 */
const setEmail = (data) => {
  myEmail.value = data.email;
  isEmailValidated.value = data.isValid;
};

/**
 * Depend on ContactEmail myPhone watcher.
 */
const setPhone = (data) => {
  myPhone.value = data.phone;
  isPhoneValidated.value = data.isValid;
};

/**
 * Show or hide required field message based on user input.
 * @param hasContact
 */
const toggleContactInfoCheck = (hasContact) => {
  if (noContactInfo.value) {
    if (hasContact) {
      noContactInfo.value.setAttribute('hidden', 'hidden');
    } else {
      noContactInfo.value.removeAttribute('hidden');
    }
  }
};

/**
 * Refresh session data (phone, email, consent) in case user has provided new info.
 * @param data
 */
const updateContactInfo = (data) => {
  const current = {
    reservationSendConfirmationRequest: {
      email: data.email,
      phone: data.phone,
      isConsentProvided: true,
      isModifyRequest: isModify.value,
    },
  };
  const updated = { ...resDetails.value.userInfo, ...current };
  store.dispatch('user/setReservationDetails', [{ property: 'userInfo', value: updated }]);
};

const validatePhoneAgain = () => {
  const ac = document.getElementById('userPhoneAC');
  const f3 = document.getElementById('userPhoneF3');
  const l4 = document.getElementById('userPhoneL4');
  const phone2 = `${ac?.value || ''}${f3?.value || ''}${l4?.value || ''}`;

  let okay = true;
  if (phone2.length > 0) {
    if (/^\d+$/.test(phone2)) {
      okay = phone2.length === 10;
    } else {
      // child component validation would have kicked in so no further parent intervention needed
      okay = false;
    }

    // toggle message on user tapping 'Send' only
    if (sendNow.value && isPhoneValidated.value) {
      toggleInvalidPhoneMsg(!okay);
    }
  }
  return okay;
};

const toggleInvalidPhoneMsg = (forceShow) => {
  if (invalidPhoneMsg.value) {
    if (forceShow) {
      // parent to show error message similar to the one in child component
      invalidPhoneMsg.value.removeAttribute('hidden');
    } else {
      invalidPhoneMsg.value.setAttribute('hidden', 'hidden');
    }
  }
};
</script>


<style lang="scss" scoped>
.modal-contact {
  form {
    button.verify {   // for verification code (future phase)
      display: inline-block;
      min-width: unset;
    }
    &::v-deep {       // pass to child components
      --field-padding: 1em;
      fieldset {
        legend {
          background-color: var(--ri-light-gray);
          padding: var(--field-padding);
          width: 100%;
        }
        .app-contact-inputs {
          padding: var(--field-padding) 0 var(--field-padding) var(--field-padding);
        }
      }
    }
    p {
      margin: -0.75em 0 1em;
      &.bad-phone {
        margin: -1.75em 0 1.5em 1em;
      }
    }
    input {
      margin-bottom: 1em;
      ~ p {
        width: unset;
      }
    }
    .msg-offset {
      margin-top: -2em;
    }
  }
  .footer-offset {
    margin-top: -3rem;
  }

  .app-button-group {
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
}

</style>
